import { Component, inject, Inject, Input, OnInit, Optional } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { LatLngBounds } from "leaflet";
import { OfflineLayerService } from '../services/offline-layer.service';
import { formatBytes } from '../../core/utils/format-bytes';
import { OFFLINE_DOCUMENT_GROUPS, OfflineDocumentGroup } from '../../core/tokens';

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule],
    selector: 'wd-offline-detail',
    templateUrl: './offline-detail.component.html',
    styleUrls: ['./offline-detail.component.css'],

})
export class OfflineDetailComponent implements OnInit {
	activeModal = inject(NgbActiveModal);
	@Input() plusLevels: 0|1|2 = 0;
	@Input() regions: LatLngBounds[] = [];
	/** True wenn Tiles bereits vollständig heruntergeladen sind */
	@Input() tilesAlreadyComplete = false;
	/** Bereits heruntergeladene Zoom-Stufe (nur relevant wenn tilesAlreadyComplete=true) */
	@Input() currentMaxZoom: number | null = null;
	/** True wenn Nutzer trotz vorhandener Tiles erneut herunterladen möchte */
	forceRedownload = false;

	/** Promise das aufgelöst wird wenn Status-Berechnung fertig ist (für Lazy-Loading) */
	@Input() statusPromise?: Promise<{ tilesAlreadyComplete: boolean; currentMaxZoom: number | null }>;
	/** True während Status geladen wird */
	isLoadingStatus = false;

	/** Optionale Dokument-Gruppen für zusätzlichen Offline-Download */
	documentGroups: OfflineDocumentGroup[] = [];
	downloadDocGroups: Record<string, boolean> = {};

	estimatedAmount = new Array<{ count: number; sizeEstimate: number; maxZoomLevel: number }>();
	error: string | null = null;

	formatBytes = formatBytes;

	constructor(
		private ols: OfflineLayerService,
		@Optional() @Inject(OFFLINE_DOCUMENT_GROUPS) private offlineDocGroups?: OfflineDocumentGroup[]
	) {
		if (offlineDocGroups?.length) {
			this.documentGroups = offlineDocGroups;
			for (const g of offlineDocGroups) {
				this.downloadDocGroups[g.label] = false;
			}
		}
	}

	ngOnInit(): void {
		if (!this.regions || this.regions.length === 0) {
			this.error = 'Kein Projektbereich definiert';
			return;
		}

		// Wenn Status-Promise übergeben wurde, auf Ergebnis warten
		if (this.statusPromise) {
			this.isLoadingStatus = true;
			this.statusPromise.then(status => {
				this.tilesAlreadyComplete = status.tilesAlreadyComplete;
				this.currentMaxZoom = status.currentMaxZoom;
				this.isLoadingStatus = false;
			}).catch(() => {
				this.isLoadingStatus = false;
			});
		}

		const zl = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
		this.loadEstimates(zl).catch(err => {
			console.error('Fehler bei Tile-Schätzung:', err);
			this.error = 'Fehler beim Berechnen der Download-Größe';
		});
	}

	private async loadEstimates(zl: number[]): Promise<void> {
		const est0 = await this.ols.tileAmountEstimate(this.regions, zl);
		this.estimatedAmount.push(est0);

		if (est0.maxZoomLevel < 18) {
			const est1 = await this.ols.tileAmountEstimate(this.regions, zl, undefined, 1);
			this.estimatedAmount.push(est1);

			if (est1.maxZoomLevel < 18) {
				const est2 = await this.ols.tileAmountEstimate(this.regions, zl, undefined, 2);
				this.estimatedAmount.push(est2);
			}
		}
	}

	onDismiss() {
		this.activeModal.dismiss();
	}

	onClose() {
		// Wenn Tiles bereits komplett sind und Nutzer nicht explizit neu laden möchte -> Skip
		const skipDownload = this.tilesAlreadyComplete && !this.forceRedownload;
		// Gewählte Dokument-Gruppen sammeln
		const selectedDocGroups = this.documentGroups
			.filter(g => this.downloadDocGroups[g.label])
			.map(g => g.label);
		this.activeModal.close({ plusLevels: this.plusLevels, skipDownload, downloadDocGroups: selectedDocGroups });
	}

	/**
	 * Prüft ob die ausgewählte Zoomstufe höher ist als die bereits heruntergeladene.
	 */
	get anyDocGroupSelected(): boolean {
		return Object.values(this.downloadDocGroups).some(v => v);
	}

	get isHigherZoomSelected(): boolean {
		if (!this.tilesAlreadyComplete || this.currentMaxZoom === null) return false;
		const selectedZoom = this.estimatedAmount[this.plusLevels]?.maxZoomLevel ?? 0;
		return selectedZoom > this.currentMaxZoom;
	}
}
